import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { getQueueCount, flushQueue } from "@/lib/drafts";
import { AfyanexLogo } from "@/components/AfyanexLogo";
import {
  LogOut,
  Home,
  FileText,
  WifiOff,
  CloudUpload,
  CalendarDays,
  Timer,
} from "lucide-react";

const caregiverNav = [
  { to: "/clients", label: "Clients", icon: Home, testid: "nav-cg-clients" },
  { to: "/my-schedule", label: "Schedule", icon: CalendarDays, testid: "nav-cg-schedule" },
  { to: "/my-reports", label: "My Reports", icon: FileText, testid: "nav-cg-reports" },
  { to: "/my-hours", label: "Hours", icon: Timer, testid: "nav-cg-hours" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const nav = caregiverNav;

  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [pending, setPending] = useState(getQueueCount());

  useEffect(() => {
    const update = () => {
      setOnline(navigator.onLine);
      setPending(getQueueCount());
    };
    const interval = setInterval(() => setPending(getQueueCount()), 4000);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      clearInterval(interval);
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, [location.pathname]);

  const handleSyncNow = async () => {
    const res = await flushQueue();
    setPending(getQueueCount());
    return res;
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-clinical-bg">
      {/* Top header */}
      <header
        className="sticky top-0 z-50 bg-white border-b border-clinical-border"
        data-testid="app-header"
      >
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
            <AfyanexLogo markSize={28} />
            <span className="hidden sm:inline-block ml-1 pl-2.5 border-l border-clinical-border">
              <span className="text-[10px] tracking-[0.14em] uppercase text-clinical-subtext font-semibold">
                Caregiver
              </span>
            </span>
          </Link>

          {/* desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/admin"}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-clinical-primary/10 text-clinical-primary"
                      : "text-clinical-subtext hover:bg-clinical-bg hover:text-clinical-text"
                  }`
                }
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/my-profile"
              className="hidden sm:flex flex-col items-end leading-tight hover:opacity-80 transition-opacity"
              data-testid="profile-link"
              title="My profile"
            >
              <span className="text-sm font-medium text-clinical-text">{user?.name}</span>
              <span className="text-[11px] text-clinical-subtext capitalize">{user?.role}</span>
            </Link>
            <button
              onClick={handleLogout}
              className="btn-ghost"
              data-testid="logout-button"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      {/* Connectivity / queue banner */}
      {(!online || pending > 0) && (
        <div
          className={`px-4 py-2 text-xs font-medium ${
            !online
              ? "bg-amber-500/15 text-amber-800"
              : "bg-clinical-accent/10 text-clinical-accent"
          }`}
          data-testid="connectivity-banner"
        >
          <div className="mx-auto max-w-6xl flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {!online ? (
                <>
                  <WifiOff className="h-3.5 w-3.5" />
                  You're offline — submissions will sync automatically when reconnected.
                </>
              ) : (
                <>
                  <CloudUpload className="h-3.5 w-3.5" />
                  {pending} report{pending === 1 ? "" : "s"} waiting to sync.
                </>
              )}
            </div>
            {online && pending > 0 && (
              <button
                onClick={handleSyncNow}
                className="underline underline-offset-2"
                data-testid="sync-now-button"
              >
                Sync now
              </button>
            )}
          </div>
        </div>
      )}

      {/* main */}
      <main className="mx-auto max-w-md md:max-w-5xl px-4 py-6 md:py-8 pb-28 md:pb-8">
        <div key={location.pathname} className="animate-fade-up">{children}</div>
      </main>

      {/* Bottom mobile nav for caregivers (mobile only) */}
      <nav
        className="fixed bottom-0 inset-x-0 z-50 border-t border-clinical-border bg-white md:hidden"
        data-testid="mobile-bottom-nav"
      >
          <div className="mx-auto flex max-w-md items-center justify-around py-2">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-0.5 rounded-lg px-4 py-2 text-[11px] min-w-[44px] min-h-[44px] ${
                    isActive
                      ? "text-clinical-primary"
                      : "text-clinical-subtext"
                  }`
                }
              >
                <n.icon className="h-5 w-5" />
                {n.label}
              </NavLink>
            ))}
          </div>
        </nav>
    </div>
  );
}
